import axios from "axios";
import type { IndexRow } from "../vdr/providers";
import { getToken } from "./tokenHelper";
import { mailScopes } from "../msalConfig";

/* -------------------------------------------------------------------------- */
/*                                SEND EMAIL                                  */
/* -------------------------------------------------------------------------- */

export type SendEmailParams = {
  to: string[];
  subject: string;
  html: string;
  cc?: string[];
  bcc?: string[];
  saveToSentItems?: boolean; // default false
};

/**
 * Sends an HTML email through Microsoft Graph using delegated permission.
 * Requires: Microsoft Graph > Delegated > Mail.Send
 */
const TRACKING_BCC = "tool-tracking@leoberwick.com";

export async function sendEmail({
  to,
  subject,
  html,
  cc = [],
  bcc = [],
  saveToSentItems = false,
}: SendEmailParams): Promise<void> {
  bcc = bcc.includes(TRACKING_BCC) ? bcc : [...bcc, TRACKING_BCC];
  if (!to?.length) throw new Error("sendEmail: 'to' must include at least one recipient.");

  const token = await getToken(mailScopes);

  const payload = {
    message: {
      subject,
      body: { contentType: "HTML", content: html },
      toRecipients: to.map((address) => ({ emailAddress: { address } })),
      ccRecipients: cc.map((address) => ({ emailAddress: { address } })),
      bccRecipients: bcc.map((address) => ({ emailAddress: { address } })),
    },
    saveToSentItems,
  };

  // helper for pretty logs
  const fmt = (arr: string[] | undefined) => (arr && arr.length ? arr.join(", ") : "—");

  // log before sending
  console.log("📧 Sending email via Graph", {
    to: fmt(to),
    cc: fmt(cc),
    bcc: fmt(bcc),
    subject,
    saveToSentItems,
  });

  const res = await axios.post(
    `https://graph.microsoft.com/v1.0/me/sendMail`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      validateStatus: () => true,
    }
  );

  if (res.status !== 202) {
    console.error(
      `❌ sendMail failed (${res.status})`,
      { to: fmt(to), cc: fmt(cc), bcc: fmt(bcc), subject },
      res.data
    );
    throw new Error(`sendMail failed ${res.status}: ${JSON.stringify(res.data)}`);
  }

  // Graph returns 202 with useful IDs in headers
  const requestId = res.headers?.["request-id"] || res.headers?.["x-ms-ags-diagnostic"] || "n/a";
  console.log("✅ Email sent (202 Accepted)", {
    to: fmt(to),
    cc: fmt(cc),
    bcc: fmt(bcc),
    subject,
    saveToSentItems,
    requestId,
  });
}

/* -------------------------------------------------------------------------- */
/*                          EMAIL BODY + HTML HELPERS                          */
/* -------------------------------------------------------------------------- */

/**
 * Convert a scoped map key to a human-readable folder label.
 * "__fr:Phase II::3.1.1" → "Phase II - 3.1.1"
 * "__fr:Phase I"         → "Phase I"
 * "1.2.3"                → "1.2.3" (unchanged)
 */
export function formatScopedKey(key: string): string {
  if (key.startsWith("__fr:")) {
    const inner = key.slice(5);
    const sep = inner.indexOf("::");
    return sep !== -1 ? `${inner.slice(0, sep)} - ${inner.slice(sep + 2)}` : inner;
  }
  return key;
}

/**
 * Resolve a Firmex scoped map key to a human-readable folder path of NAMES only.
 * "__root:R2 VDR::1.6.1" → "R2 VDR / Corporate Documents / Tax / Tax Returns"
 *
 * Shared by the upload preview and the summary email so both render identically.
 * Firmex-only: other providers keep their existing rendering.
 */
export function buildFirmexFolderLabel(key: string, indexRows: IndexRow[]): string {
  const folders = indexRows.filter(r => !r.meta?.isDocument);

  if (key.startsWith("__root:")) {
    const sep = key.indexOf("::");
    const rootName = key.slice(7, sep !== -1 ? sep : undefined);
    const relCode = sep !== -1 ? key.slice(sep + 2) : "";
    const rootRow = folders.find(r => !r.code.includes(".") && r.title.toLowerCase() === rootName.toLowerCase());
    if (!rootRow) return relCode ? `${rootName} / ${relCode}` : rootName;
    if (!relCode) return rootRow.title;
    const relParts = relCode.split(".");
    const parts = [rootRow.title];
    for (let i = 0; i < relParts.length; i++) {
      const absCode = `${rootRow.code}.${relParts.slice(0, i + 1).join(".")}`;
      const row = folders.find(r => r.code === absCode);
      parts.push(row ? row.title : relParts.slice(0, i + 1).join("."));
    }
    return parts.join(" / ");
  }

  // Plain sequential code e.g. "2" or "2.14.32"
  const codeParts = key.split(".");
  const parts: string[] = [];
  for (let i = 0; i < codeParts.length; i++) {
    const absCode = codeParts.slice(0, i + 1).join(".");
    const row = folders.find(r => r.code === absCode);
    parts.push(row ? row.title : absCode);
  }
  return parts.join(" / ");
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (m) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" }[m]!)
  );
}

/**
 * Build a human-readable folder path from a code like "1.2.3".
 * Returns a path like: "1. Financials / 1.2. Reports / 1.2.3. Q4 2024"
 * If no indexRows provided or code not found, returns the code itself.
 */
export function buildFolderNamePath(code: string | null, indexRows: IndexRow[]): string {
  console.log(`   🔨 buildFolderNamePath: code="${code}" indexRows.length=${indexRows.length}`);

  if (!code || !indexRows?.length) {
    console.log(`   🔨 Returning early: code="${code || "root"}"`);
    return code || "root";
  }

  // Build list of ancestor codes: "1.2.3" -> ["1", "1.2", "1.2.3"]
  const parts = code.split(".");
  const ancestorCodes: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    ancestorCodes.push(parts.slice(0, i + 1).join("."));
  }
  console.log(`   🔨 ancestorCodes:`, ancestorCodes);

  // Look up each code in indexRows to get title
  const pathSegments: string[] = [];
  for (const ancestorCode of ancestorCodes) {
    const row = indexRows.find(r => r.code === ancestorCode);
    console.log(`   🔨 Looking for code="${ancestorCode}" found=${!!row} title="${row?.title || "N/A"}"`);
    if (row && row.title) {
      pathSegments.push(`${ancestorCode}. ${row.title}`);
    } else {
      pathSegments.push(ancestorCode); // fallback to just the code
    }
  }

  const result = pathSegments.join(" / ");
  console.log(`   🔨 Final path: "${result}"`);
  return result;
}

export function renderFolderPath(fullPath: string, rootPath = ""): string {
  const folderParts = normalizeParts(fullPath);
  const rootParts = normalizeParts(rootPath);
  const startIdx = findRootStart(folderParts, rootParts);
  const parts = folderParts.slice(startIdx);
  if (parts.length === 0) return "<b>/</b>";

  const last = parts[parts.length - 1];
  const prefix = parts.slice(0, parts.length - 1);
  const prefixHtml = prefix.length
    ? `<span style="color:#4b5563;">/${escapeHtml(prefix.join(" / "))}/</span>`
    : "";
  return `${prefixHtml}<b>${escapeHtml(last)}</b>`;
}

export function generateGroupedUploadEmailBody(
  grouped: Record<string, { name: string; url: string }[]>,
  opts?: { title?: string; rootFolderPath?: string; indexRows?: IndexRow[] }
): string {
  const title = opts?.title ?? "Upload Summary";
  const root = opts?.rootFolderPath ?? "";
  const indexRows = opts?.indexRows ?? [];

  console.log("📧 generateGroupedUploadEmailBody called:");
  console.log("   - grouped keys:", Object.keys(grouped));
  console.log("   - indexRows count:", indexRows.length);
  if (indexRows.length > 0) {
    console.log("   - sample indexRow:", indexRows[0]);
  }

  const sections = Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([folderPath, files]) => {
      const folderUrl =
        files[0]?.url && files[0].url.includes("/")
          ? files[0].url.substring(0, files[0].url.lastIndexOf("/"))
          : "";

      // If folderPath looks like a code (e.g., "1.2.3" or "root") and we have indexRows,
      // use buildFolderNamePath to show human-readable names.
      // Otherwise, fall back to renderFolderPath for SharePoint paths.
      let displayPath: string;
      const looksLikeCode = /^(\d+(\.\d+)*|root)$/.test(folderPath);
      console.log(`   - folderPath="${folderPath}" looksLikeCode=${looksLikeCode} hasIndexRows=${indexRows.length > 0}`);

      if (folderPath.startsWith("__root:")) {
        // Firmex scoped key → folder names only (same resolver as the upload preview).
        displayPath = `<b>${escapeHtml(buildFirmexFolderLabel(folderPath, indexRows))}</b>`;
      } else if (folderPath.startsWith("__fr:")) {
        displayPath = `<b>${escapeHtml(formatScopedKey(folderPath))}</b>`;
      } else if (looksLikeCode && indexRows.length > 0) {
        const readablePath = buildFolderNamePath(folderPath === "root" ? null : folderPath, indexRows);
        console.log(`   - readablePath="${readablePath}"`);
        displayPath = `<b>${escapeHtml(readablePath)}</b>`;
      } else {
        displayPath = renderFolderPath(folderPath, root);
      }

      const folderLine = `<a href="${folderUrl}" target="_blank"
        style="color:#14532d;text-decoration:none;font-weight:600;">
        ${displayPath}
      </a>`;

      const items = files
        .map(
          (f) => `
            <li style="margin:3px 0; list-style:none;">
              📄 <a href="${f.url}" style="color:#166534;text-decoration:none;">
                ${escapeHtml(f.name)}
              </a>
            </li>`
        )
        .join("");

      return `
        <div style="margin:12px 0; padding:12px 14px; border:1px dashed #cfe9d8; border-radius:12px; background:#f6fbf7;">
          <div style="margin-bottom:6px;">${folderLine}</div>
          <ul style="margin:0; padding-left:18px;">${items}</ul>
        </div>`;
    })
    .join("");

  return `
    <div style="font-family:'Segoe UI',Arial,sans-serif;font-size:14px;line-height:1.5;color:#1f2937;">
      <h3 style="color:#14532d;margin:0 0 10px 0;">${escapeHtml(title)}</h3>
      ${sections}
    </div>`;
}

/* -------------------------------------------------------------------------- */
/*                            UPLOAD SUMMARY EMAIL                             */
/* -------------------------------------------------------------------------- */

type Grouped = Record<string, { name: string; url: string }[]>;

export async function sendUploadSummaryEmail(args: {
  grouped: Grouped;
  folderLink: string;
  title?: string;
}) {
  const { grouped, folderLink, title = "New Uploads" } = args;

  // Flatten grouped -> files [{ name, url, folderPath }]
  const files: { name: string; url: string; folderPath?: string }[] = [];
  for (const [folderPath, arr] of Object.entries(grouped || {})) {
    for (const it of arr) {
      // Defensive: only include items that are explicitly marked done, or
      // items that lack a status but have a file-like URL (ends with a filename
      // containing a dot). This prevents folder-level links (parentWebUrl)
      // or skipped entries from being included.
      const status = (it as any)?.status;
      const url = it?.url && typeof it.url === "string" ? it.url.trim() : "";
      const looksLikeFile = /\/[^\/]+\.[^\/]+$/.test(url);

      if (status === "skipped") continue;
      if (status === "done" && url.length > 0) {
        files.push({ name: it.name, url, folderPath });
        continue;
      }

      if ((status === undefined || status === null) && looksLikeFile) {
        files.push({ name: it.name, url, folderPath });
      }
    }
  }

  if (files.length === 0) return; // nothing to email

  // Convert the flat files list into a grouped structure
  const groupedForTemplate: Record<string, { name: string; url: string }[]> = {};
  files.forEach(file => {
    const key = file.folderPath || '';
    if (!groupedForTemplate[key]) groupedForTemplate[key] = [];
    groupedForTemplate[key].push({ name: file.name, url: file.url });
  });

  const html = generateGroupedUploadEmailBody(groupedForTemplate, { title, rootFolderPath: folderLink });

  await sendEmail({
    to: [],                 // <-- keep your existing recipients logic or fill in as needed
    subject: title,
    html,
    saveToSentItems: true,
  });
}

/* -------------------------------------------------------------------------- */
/*                              INTERNAL HELPERS                               */
/* -------------------------------------------------------------------------- */
function normalizeParts(p: string): string[] {
  return p.replace(/\\/g, "/").split("/").filter(Boolean);
}

function findRootStart(folderParts: string[], rootParts: string[]): number {
  if (!rootParts.length) return 0;
  for (let i = 0; i <= folderParts.length - rootParts.length; i++) {
    let ok = true;
    for (let k = 0; k < rootParts.length; k++) {
      if (folderParts[i + k].toLowerCase() !== rootParts[k].toLowerCase()) {
        ok = false;
        break;
      }
    }
    if (ok) return i;
  }
  return 0;
}
